import { Check, CircleQuestionMark } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AccordionItem } from "@krea/shared/design-system/AccordionItem";
import { Badge } from "@krea/shared/design-system/Badge";
import { Button } from "@krea/shared/design-system/Button";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { useFormatCurrency } from "@krea/shared/hooks/useFormatCurrency";

import type { ILoanWidgetResultProps } from "../LoanWidgetResult";
import { ForetagarnaResultBlock } from "./ForetagarnaResultBlock";
import { HowWeEstimatedTooltipContent } from "./HowWeEstimatedTooltipContent";
import { ImproveChancesContent } from "./ImproveChancesContent";
import { LikelihoodBox } from "./LikelihoodBox";
import { StartApplicationSection } from "./StartApplicationSection";
import { WhatHappensNextSteps } from "./WhatHappensNextSteps";

/** Result view for the estimated tier: shows the likelihood, the estimate, and the apply CTA. */
export const EstimateResult = ({
  countryCode,
  currency,
  locale,
  organisationName,
  preliminaryBid,
  onGoBack,
  onContinueToApply,
  utmSourceIdentifier,
  renderReviewBadges,
  showCta = true,
  showUsps = true,
  portalContainer,
  onContactUs,
}: ILoanWidgetResultProps) => {
  const { t } = useTranslation("loanWidget");
  const [formattedCurrencyValue] = useFormatCurrency(locale, { currency });

  const estimatedLowerAmount = preliminaryBid?.estimatedMinLoanAmount;
  const estimatedAmount = preliminaryBid?.estimatedMaxLoanAmount;

  const probabilityKey = preliminaryBid.probabilityLoanApproved;
  const isVeryHigh = probabilityKey === "high";
  const isHigh = probabilityKey === "medium";
  const isLow = probabilityKey === "low";

  const cardTitle = isVeryHigh
    ? t("loanWidget.veryHighLikelihood")
    : isHigh
      ? t("loanWidget.highLikelihood")
      : t("loanWidget.possibleLikelihood");

  const showForetagarna =
    utmSourceIdentifier === "foretagarna_sverige_service_ab" &&
    !!estimatedAmount;

  return (
    <div>
      {/* ── Result card ── */}
      <div data-testid="result-card" className="rounded-2xl mb-5">
        {/* Likelihood label */}
        <LikelihoodBox
          title={cardTitle}
          isMint={isVeryHigh || isHigh}
          organisationName={organisationName}
          onGoBack={onGoBack}
          portalContainer={portalContainer}
        />

        {/* Estimated range accordion */}
        {estimatedAmount !== undefined &&
          estimatedLowerAmount !== undefined && (
            <AccordionItem
              className="border-gray-200 bg-white rounded-xl mb-4"
              titleStyling="flex flex-col font-normal"
              title={
                <>
                  <span className="flex items-center gap-1.5 text-label">
                    {t("loanWidget.estimatedRange")}
                    <ResponsiveTooltip
                      trigger={
                        <CircleQuestionMark
                          size={14}
                          className="cursor-pointer text-gray-600"
                        />
                      }
                      side="top"
                      align="center"
                      className="max-w-xs p-3"
                      portalContainer={portalContainer}
                    >
                      <HowWeEstimatedTooltipContent />
                    </ResponsiveTooltip>
                  </span>
                  {/* Slightly smaller on mobile; original text-lg on md+. */}
                  <span className="text-base font-semibold text-black md:text-lg">
                    {formattedCurrencyValue(estimatedLowerAmount)}
                    {" – "}
                    {formattedCurrencyValue(estimatedAmount)}
                  </span>
                </>
              }
            >
              {isVeryHigh && (
                <p
                  data-testid="larger-amounts"
                  className="text-sm text-black mb-0"
                >
                  {t("loanWidget.largerAmountsPossible")}
                </p>
              )}
              {(isHigh || isLow) && (
                <ImproveChancesContent onContactUs={onContactUs} />
              )}
            </AccordionItem>
          )}

        {/* Primary CTA + trust badges + "what happens next" */}
        {showCta && onContinueToApply && (
          <>
            <Button
              data-testid="cta-button"
              size="lg"
              className="w-full bg-primary hover:bg-primary/90 mb-3"
              onClick={onContinueToApply}
            >
              {t("loanWidget.continueToApplication")}
            </Button>
            <ul className="flex list-none flex-wrap justify-center gap-2 p-0 mb-4">
              {[
                t("loanWidget.trustTagNoCreditCheckInThisStep"),
                t("loanWidget.trustTagNoCommitment"),
                t("loanWidget.trustTagFreeOfCharge"),
              ].map((tag) => (
                <li key={tag}>
                  <Badge variant="successLight">
                    <Check className="size-4 mr-1" />
                    <span>{tag}</span>
                  </Badge>
                </li>
              ))}
            </ul>

            {/* "What happens next" steps — an outlined card, so it needs no
                divider above it. */}
            <WhatHappensNextSteps />
          </>
        )}
      </div>

      {/* ── Reviews + USPs — below card, after org name ── */}
      {showCta && onContinueToApply && (
        <div className="mb-4">
          <StartApplicationSection
            handleSubmit={onContinueToApply}
            renderReviewBadges={renderReviewBadges}
            countryCode={countryCode}
            showUsps={showUsps}
            showButton={false}
          />
        </div>
      )}

      {/* ── Företagarna block ── */}
      <ForetagarnaResultBlock
        show={showForetagarna}
        amount={estimatedAmount}
        locale={locale}
        currency={currency}
      />
    </div>
  );
};
